/**
 * @format
 */
import { Component, HostListener, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CustomAttainmentWorkflowApplication, CustomStudyDraft, OtmId, Plan } from 'common-typescript/types';
import { take } from 'rxjs';
import {
    AppErrorHandler,
    FileItem,
    HeaderService,
    STUDENT_APPLICATION_TYPE,
    StudyRightEntityService,
    UiRouterService,
} from 'sis-components';
import { ApplicationCreationService } from '../../../../common/service/application-creation.service';

@Component({
    selector: 'app-custom-attainment-workflow-application-wizard',
    templateUrl: './custom-attainment-workflow-application-wizard.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class CustomAttainmentWorkflowApplicationWizardComponent implements OnInit {
    @Input() plan: Plan;
    @Input() customStudyDraftId?: OtmId;

    readonly wizardStepKeys = [
        'PROFILE.APPLICATIONS.CUSTOM_ATTAINMENT.PHASE_1',
        'PROFILE.APPLICATIONS.CUSTOM_ATTAINMENT.PHASE_2',
        'PROFILE.APPLICATIONS.CUSTOM_ATTAINMENT.PHASE_3',
    ];

    currentStep = 0;

    workflowApplication: Partial<CustomAttainmentWorkflowApplication> = {};
    attachments: FileItem[] = [];
    customStudyDraft: CustomStudyDraft;
    uploading = false;

    constructor(
        private applicationCreationService: ApplicationCreationService,
        private headerService: HeaderService,
        private studyRightEntityService: StudyRightEntityService,
        private translate: TranslateService,
        private uiRouterService: UiRouterService,
        private appErrorHandler: AppErrorHandler,
    ) {}

    ngOnInit(): void {
        this.workflowApplication = {
            planId: this.plan?.id,
            type: STUDENT_APPLICATION_TYPE.CUSTOM_ATTAINMENT_APPLICATION,
        };

        this.studyRightEntityService
            .resolveStudyRightForApplication(this.plan)
            .pipe(take(1), this.appErrorHandler.defaultErrorHandler())
            .subscribe({
                next: studyRight => (this.workflowApplication.studyRightId = studyRight?.id),
            });

        if (this.customStudyDraftId) {
            this.customStudyDraft = this.plan?.customStudyDrafts?.find(draft => draft?.id === this.customStudyDraftId);
            this.workflowApplication.customStudyDraft = this.customStudyDraft ? { ...this.customStudyDraft } : undefined;
        }
    }

    @HostListener('window:beforeunload', ['$event'])
    confirmDiscardChanges(event: BeforeUnloadEvent) {
        event.preventDefault();
        event.returnValue = true;
    }

    onFormValueChange(changes: Partial<CustomAttainmentWorkflowApplication>): void {
        if (changes) {
            this.workflowApplication = { ...this.workflowApplication, ...changes };
        }
    }

    onAttachmentsChange(attachments: FileItem[]): void {
        this.attachments = attachments ?? [];
    }

    isFirstStep(): boolean {
        return this.currentStep === 0;
    }

    isLastStep(): boolean {
        return this.currentStep === this.wizardStepKeys.length - 1;
    }

    exit(): void {
        this.uiRouterService.goToPreviousState();
    }

    previous(): void {
        if (!this.isFirstStep()) {
            this.currentStep -= 1;
        }
    }

    continue(changes: Partial<CustomAttainmentWorkflowApplication>): void {
        this.onFormValueChange(changes);
        if (!this.isLastStep()) {
            if (this.isFirstStep()) {
                const applicationTypeName = this.translate.instant(
                    `STUDENT_APPLICATIONS.TYPE.${STUDENT_APPLICATION_TYPE.CUSTOM_ATTAINMENT_APPLICATION}`,
                );
                this.headerService.setCustomPageTitle(`${applicationTypeName}: ${this.workflowApplication?.name}`);
            }
            this.currentStep += 1;
        }
    }

    submit(changes: Partial<CustomAttainmentWorkflowApplication>) {
        if (!this.uploading) {
            this.onFormValueChange(changes);
            this.uploading = true;
            this.applicationCreationService
                .createWorkflowApplication(
                    this.workflowApplication,
                    this.attachments,
                    'student.logged-in.profile.applications.custom-attainment-workflow',
                )
                .pipe(take(1), this.appErrorHandler.defaultErrorHandler())
                .subscribe({
                    complete: () => {
                        this.uploading = false;
                    },
                });
        }
    }
}
